<template>
  <el-dialog :title="title" :visible="visible" width="560px" :close-on-click-modal="false" :before-close="close">
    <template v-if="mode === 'link'">
      <p class="dialog-help">Link “{{ record.name }}” to an existing {{ entity === 'curator' ? 'account' : 'collection' }}. Its internal fields and history will stay with the live record.</p>
      <el-alert title="Choose a live record without existing internal tracking" description="Existing tracking will never be overwritten. This action does not create an account or publish a collection." type="info" :closable="false" show-icon />
      <el-select v-model="targetId" filterable remote :remote-method="searchOptions" :loading="loadingOptions" placeholder="Search by name, email, or ID" class="wide-select" aria-label="Live record to link" :disabled="saving">
        <el-option v-for="option in options" :key="option.id" :value="option.id" :label="optionLabel(option)" />
      </el-select>
    </template>
    <el-form v-else label-position="top" @submit.native.prevent="save">
      <template v-if="isNewCurator">
        <p class="dialog-help">Enter the curator's email. If no LearningFirst account uses this email, one will be created automatically. The account is added to this list immediately with its planning fields ready to fill in.</p>
        <el-form-item label="Account email" required>
          <el-input v-model.trim="form.email" type="email" maxlength="254" :disabled="saving" autofocus placeholder="curator@example.com" label="Account email" />
        </el-form-item>
      </template>
      <template v-else>
        <p class="dialog-help">Plan the work before creating a live account or collection. This entry is visible only in the admin system.</p>
        <el-form-item :label="entity === 'curator' ? 'Planned curator name' : 'Collection idea / title'" required>
          <el-input v-model="form.name" maxlength="200" :disabled="saving" autofocus label="Plan name" />
        </el-form-item>
        <el-form-item v-if="entity === 'curator'" label="Planned account email (optional)">
          <el-input v-model.trim="form.email" type="email" maxlength="254" :disabled="saving" label="Planned account email" />
        </el-form-item>
        <el-form-item v-else label="Curator account or planned curator (optional)">
          <el-select v-model="form.user_id" clearable filterable remote :remote-method="searchOptions" :loading="loadingOptions" placeholder="Search live or planned curators" class="owner-select" :disabled="saving" aria-label="Plan curator">
            <el-option v-for="option in options" :key="option.id" :value="option.id" :label="optionLabel(option)" />
          </el-select>
        </el-form-item>
      </template>
    </el-form>
    <el-alert v-if="error" :title="error" type="error" :closable="false" show-icon />
    <span slot="footer">
      <el-button :disabled="saving" @click="close">Cancel</el-button>
      <el-button type="primary" :loading="saving" :disabled="footerDisabled" @click="save">{{ footerLabel }}</el-button>
    </span>
  </el-dialog>
</template>

<script>
import { listAdminCurators, listAdminCollections, saveAdminOperationsPlan, linkAdminOperationsPlan, findAdminCuratorByEmail, updateAdminOperations } from '@/apis/lfAdmin'
export default {
  name: 'AdminOperationsPlanDialog',
  props: { visible: Boolean, entity: { type: String, required: true }, record: { type: Object, default: null }, defaultOwner: { type: String, default: '' }, mode: { type: String, default: 'edit' } },
  data() { return { form: { name: '', email: '', user_id: '' }, options: [], targetId: '', saving: false, loadingOptions: false, error: '', requestId: 0 } },
  computed: {
    isNewCurator() { return this.entity === 'curator' && !this.record && this.mode === 'edit' },
    title() { if (this.isNewCurator) return 'Add curator'; return this.mode === 'link' ? 'Link to a live record' : `${this.record ? 'Edit' : 'New'} ${this.entity === 'curator' ? 'planned curator' : 'collection idea'}` },
    footerDisabled() { if (this.mode === 'link') return !this.targetId; if (this.isNewCurator) return !this.form.email.trim(); return !this.form.name.trim() },
    footerLabel() { if (this.mode === 'link') return 'Link live record'; if (this.isNewCurator) return 'Add curator'; return this.record ? 'Save plan' : 'Create plan' }
  },
  watch: {
    visible: { immediate: true, handler(value) {
      if (!value) return
      this.error = ''; this.targetId = ''
      this.form = { name: this.record ? this.record.name : '', email: this.record ? (this.record.user_email || '') : '', user_id: this.record ? (this.record.user_id || '') : this.defaultOwner }
      this.options = this.form.user_id ? [{ id: this.form.user_id, name: (this.record && this.record.user_name) || this.form.user_id }] : []
      if (this.entity === 'collection' || this.mode === 'link') this.searchOptions('')
    } }
  },
  beforeDestroy() { this.requestId++ },
  methods: {
    result(response) {
      const result = response && response.data
      if (!result || Number(result.code) !== 0 || !result.data) throw new Error((result && result.msg) || 'Unable to save this plan.')
      return result.data
    },
    close() { if (!this.saving) this.$emit('update:visible', false) },
    optionLabel(option) { return `${option.name || option.user_email || option.id}${option.record_kind === 'planned' ? ' (planned)' : ''}${option.user_email && option.name ? ` · ${option.user_email}` : ''}` },
    async searchOptions(q) {
      const requestId = ++this.requestId
      this.loadingOptions = true
      try {
        const params = { q, scope: 'all', page_size: 50 }
        if (this.mode === 'link') params.record_kind = 'existing'
        const loader = this.mode === 'link' && this.entity === 'collection' ? listAdminCollections : listAdminCurators
        const data = this.result(await loader(params))
        if (requestId !== this.requestId) return
        const selected = this.options.find(o => o.id === this.form.user_id)
        this.options = data.rows
        if (selected && !this.options.some(o => o.id === selected.id)) this.options.unshift(selected)
      } catch (error) { if (requestId === this.requestId) this.error = error.message }
      finally { if (requestId === this.requestId) this.loadingOptions = false }
    },
    async save() {
      if (this.saving) return
      if (this.isNewCurator) { if (this.form.email.trim() !== '') return this.saveNewCurator(); return }
      if (this.mode === 'link' ? !this.targetId : !this.form.name.trim()) return
      this.saving = true; this.error = ''
      try {
        if (this.mode === 'link') this.result(await linkAdminOperationsPlan({ entity: this.entity, id: this.record.id, target_id: this.targetId }))
        else {
          const params = { entity: this.entity, ...this.form }
          if (this.record) { params.id = this.record.id; params.identity_version = this.record.identity_version }
          this.result(await saveAdminOperationsPlan(params))
        }
        this.$emit('saved')
        this.$emit('update:visible', false)
        this.$message.success(this.mode === 'link' ? 'Linked to the live record. Internal tracking preserved.' : 'Plan saved.')
      } catch (error) { this.error = error.message }
      finally { this.saving = false }
    },
    async saveNewCurator() {
      this.saving = true; this.error = ''
      try {
        const account = this.result(await findAdminCuratorByEmail({ email: this.form.email.trim(), create_if_missing: true }))
        if (!account.tracked) this.result(await updateAdminOperations({ entity: 'curator', id: account.id, version: account.version || 0, field: 'tracked', value: true }))
        this.$emit('saved', 'tracked')
        this.$emit('update:visible', false)
        this.$message.success(`${account.created ? 'Account created. ' : ''}${account.name || account.email} is now tracked as a curator.`)
      } catch (error) { this.error = error.message }
      finally { this.saving = false }
    }
  }
}
</script>
<style scoped>
.dialog-help { margin-top: 0; color: #64758a; line-height: 1.6; }
.wide-select { width: 100%; margin: 20px 0; }
.owner-select { width: 100%; }
</style>
